import React, { Suspense } from "react";
import { Link } from "react-router-dom";
import { usePerformanceMonitor } from "../../hooks/usePerformanceMonitor";
import { useResponsiveBreakpoints } from "../../hooks/useResponsiveBreakpoints";

// Optimized component imports with lazy loading for better code splitting
const HeroSection = React.lazy(() => import("./LandingPageComponents/HeroSection"));
const SearchAndFeaturedSection = React.lazy(() => import("./LandingPageComponents/SearchAndFeaturedSection"));
const FeaturesSection = React.lazy(() => import('./LandingPageComponents/FeaturesSection'));
const FoundersSection = React.lazy(() => import('./LandingPageComponents/FoundersSection'));
const TestimonialsSection = React.lazy(() => import('./LandingPageComponents/TestimonialsSection'));

// Optimized loading components
const ComponentSkeleton = React.memo(({ height = "500px" }) => (
  <div style={{
    width: "100%",
    height,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(20, 72, 116, 0.1) 0%, rgba(1, 162, 241, 0.05) 100%)",
    animation: "pulse 1.5s ease-in-out infinite alternate"
  }}>
    <div style={{
      width: "60px",
      height: "60px",
      border: "4px solid #f85008",
      borderTop: "4px solid transparent",
      borderRadius: "50%",
      animation: "spin 1s linear infinite"
    }} />
    <style>{`
      @keyframes pulse {
        0% { opacity: 0.6; }
        100% { opacity: 1; }
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
));

ComponentSkeleton.displayName = 'ComponentSkeleton';

// Error boundary for better error handling
class ComponentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('LandingPage component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: "2rem",
          textAlign: "center",
          color: "#144874",
          fontFamily: "Montserrat, sans-serif"
        }}>
          <h3>Something went wrong loading this section.</h3>
          <p>Please refresh the page to try again.</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: "#f85008",
              color: "white",
              padding: "0.8rem 1.5rem",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginTop: "1rem"
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Optimized LandingPage component
 * - Progressive component loading with Suspense
 * - Performance monitoring
 * - Error boundaries for resilience
 * - Responsive design with global hook
 * - Minimal re-renders
 */
const LandingPage = React.memo(() => {
  // Performance monitoring
  const { startTiming, endTiming, exportMetrics } = usePerformanceMonitor({
    componentName: 'LandingPage',
    enableCoreWebVitals: true,
    enableCustomMetrics: true,
    enableMemoryMonitoring: true,
    onMetric: (metric) => {
      // Log important metrics in development
      if (process.env.NODE_ENV === 'development' && 
          ['LCP', 'FID', 'CLS'].includes(metric.name)) {
        console.log(`🎯 [Core Web Vital] ${metric.name}:`, metric.value, metric.unit);
      }
    }
  });

  // Global responsive state
  const { isMobile } = useResponsiveBreakpoints();

  // Performance timing for entire page load
  React.useEffect(() => {
    startTiming('landing-page-load');
    
    // Export metrics after page is loaded
    const exportTimer = setTimeout(() => {
      const metrics = exportMetrics();
      
      // Send to analytics service in production
      if (process.env.NODE_ENV === 'production' && window.gtag) {
        window.gtag('event', 'performance_metrics', {
          custom_parameter: JSON.stringify(metrics)
        });
      }
    }, 5000); // Export after 5 seconds

    return () => {
      endTiming('landing-page-load');
      clearTimeout(exportTimer);
    };
  }, [startTiming, endTiming, exportMetrics]);

  // Preload critical resources
  React.useEffect(() => {
    // Preload critical fonts
    const fontLink = document.createElement('link');
    fontLink.rel = 'preload';
    fontLink.as = 'font';
    fontLink.type = 'font/woff2';
    fontLink.crossOrigin = 'anonymous';
    fontLink.href = '/fonts/Montserrat-Regular.woff2'; // Adjust path as needed
    document.head.appendChild(fontLink);

    return () => {
      document.head.removeChild(fontLink);
    };
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
        maxWidth: "100vw",
        position: "relative",
      }}
    >
      <style>{`
        @media (max-width: 480px) {
          body {
            overflow-x: hidden !important;
            max-width: 100vw !important;
          }
          
          * {
            -webkit-tap-highlight-color: transparent;
          }
          
          /* Reduce animations for better mobile performance */
          *, *::before, *::after {
            animation-duration: 0.3s !important;
            transition-duration: 0.2s !important;
          }
          
          /* Disable parallax on mobile */
          .parallax {
            background-attachment: fixed !important;
          }
          
          /* Optimize scrolling */
          .scroll-container {
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
          }
        }
      `}</style>
      <main style={{ 
        flex: "1 0 auto",
        ...(isMobile && {
          padding: "0",
          maxWidth: "100vw",
          overflowX: "hidden"
        })
      }}>
        {/* Hero Section - Critical, loads immediately */}
        <ComponentErrorBoundary>
          <Suspense fallback={<ComponentSkeleton height="100vh" />}>
            <HeroSection />
          </Suspense>
        </ComponentErrorBoundary>

        {/* Founders Section - High priority */}
        <ComponentErrorBoundary>
          <Suspense fallback={<ComponentSkeleton height="400px" />}>
            <FoundersSection />
          </Suspense>
        </ComponentErrorBoundary>

        {/* Search and Featured Section - High priority */}
        <ComponentErrorBoundary>
          <Suspense fallback={<ComponentSkeleton height="600px" />}>
            <SearchAndFeaturedSection />
          </Suspense>
        </ComponentErrorBoundary>

        {/* Features Section - Medium priority */}
        <ComponentErrorBoundary>
          <Suspense fallback={<ComponentSkeleton height="800px" />}>
            <FeaturesSection />
          </Suspense>
        </ComponentErrorBoundary>

        {/* Testimonials Section - Lower priority */}
        <ComponentErrorBoundary>
          <Suspense fallback={<ComponentSkeleton height="500px" />}>
            <TestimonialsSection />
          </Suspense>
        </ComponentErrorBoundary>
      </main>

      {/* Hidden dev entry: Browse Talent V2 design test */}
      <Link
        to="/browse-talent-v2"
        aria-label="Browse Talent V2 preview"
        style={{
          position: "fixed",
          bottom: "8px",
          left: "8px",
          width: "14px",
          height: "14px",
          borderRadius: "50%",
          backgroundColor: "#f85008",
          opacity: 0.12,
          zIndex: 5000,
          display: "block",
        }}
      />
    </div>
  );
});

LandingPage.displayName = 'LandingPage';

export default LandingPage;